import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Container,
  alpha,
  useTheme,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getMyProgress, getStudentProgress, clearProgress } from "../../state/slices/progress";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../components/loading/loading";
import { School, Quiz, Collections, Schedule } from "@mui/icons-material";

const ProgressDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { progressData, loading } = useSelector((state) => state.progress);
  const user = useSelector((state) => state.user.user);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [children, setChildren] = useState([]);

  useEffect(() => {
    dispatch(getMyProgress()).then((action) => {
      if (action.payload?.children) {
        setChildren(action.payload.children || []);
      }
    });
    return () => {
      dispatch(clearProgress());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedChildId) {
      dispatch(getStudentProgress(selectedChildId));
    } else {
      dispatch(getMyProgress());
    }
  }, [selectedChildId, dispatch]);

  if (loading || !progressData) {
    return <Loading />;
  }

  const isParent = user?.roles?.includes("PARENT");
  const isBusiness = user?.roles?.includes("BUSINESS");
  const showChildrenSelector = (isParent || isBusiness) && children.length > 0;

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
            }}
          >
            {showChildrenSelector && selectedChildId
              ? t('progress.childProgress') || 'Child Progress'
              : showChildrenSelector
              ? t('progress.combinedProgress') || 'Combined Progress'
              : t('progress.yourProgressDashboard')}
          </Typography>
          {showChildrenSelector && (
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{isParent ? t('progress.selectChild') || 'Select Child' : t('progress.selectEmployee') || 'Select Employee'}</InputLabel>
              <Select
                value={selectedChildId || ''}
                onChange={(e) => setSelectedChildId(e.target.value || null)}
                label={isParent ? t('progress.selectChild') || 'Select Child' : t('progress.selectEmployee') || 'Select Employee'}
              >
                <MenuItem value="">{t('progress.all') || 'All'}</MenuItem>
                {children.map((child) => (
                  <MenuItem key={child.studentId} value={child.studentId}>
                    {child.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Sessions Statistics */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Schedule sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{t('progress.sessions')}</Typography>
                    <Typography variant="h4">{progressData.sessions?.total || 0}</Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {t('progress.completed')}: {progressData.sessions?.completed || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('progress.upcoming')}: {progressData.sessions?.upcoming || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Quiz Statistics */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Quiz sx={{ fontSize: 40, color: theme.palette.secondary.main, mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{t('progress.quizzes')}</Typography>
                    <Typography variant="h4">{progressData.quizzes?.total || 0}</Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('progress.averageScore')}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={progressData.quizzes?.averageScore || 0}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="body2" fontWeight="bold">
                      {progressData.quizzes?.averageScore || 0}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Flashcard Statistics */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Collections sx={{ fontSize: 40, color: theme.palette.success.main, mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{t('progress.flashcards')}</Typography>
                    <Typography variant="h4">{progressData.flashcards?.totalSets || 0}</Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('progress.mastery')}: {progressData.flashcards?.masteredCards || 0} / {progressData.flashcards?.totalCards || 0} {t('progress.cards')}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progressData.flashcards?.masteryPercentage || 0}
                    sx={{ mt: 1, height: 8, borderRadius: 1 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Subject Breakdown - Quizzes */}
          {progressData.quizzes?.bySubject && Object.keys(progressData.quizzes.bySubject).length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('progress.quizPerformanceBySubject')}
                  </Typography>
                  {Object.entries(progressData.quizzes.bySubject).map(([subject, stats]) => (
                    <Box key={subject} sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body2">{subject}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {stats.averageScore}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stats.averageScore}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {stats.totalAttempts} {t('progress.attempts')}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Subject Breakdown - Flashcards */}
          {progressData.flashcards?.bySubject && Object.keys(progressData.flashcards.bySubject).length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('progress.flashcardMasteryBySubject')}
                  </Typography>
                  {Object.entries(progressData.flashcards.bySubject).map(([subject, stats]) => (
                    <Box key={subject} sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body2">{subject}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {stats.masteryPercentage}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stats.masteryPercentage}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {stats.masteredCards} / {stats.totalCards} {t('progress.cardsMastered')}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Recent Activity */}
          {progressData.recentActivity && progressData.recentActivity.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('progress.recentActivity')}
                  </Typography>
                  {progressData.recentActivity.map((activity, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 1,
                        borderBottom: index < progressData.recentActivity.length - 1 ? "1px solid" : "none",
                        borderColor: "divider",
                      }}
                    >
                      <Box sx={{ mr: 2 }}>
                        {activity.type === "quiz" ? (
                          <Quiz color="secondary" />
                        ) : (
                          <Collections color="success" />
                        )}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1">{activity.title}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {activity.subject} • {new Date(activity.date).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box>
                        {activity.type === "quiz" ? (
                          <Chip label={`${activity.score}%`} color="secondary" size="small" />
                        ) : (
                          <Chip label={activity.mastery} size="small" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default ProgressDashboard;

